import { type ReactNode, type Ref } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { cn } from '@/lib/utils';

interface FormPageLayoutProps {
  /** Usually a string; a node when the page makes its title editable in place. */
  title: ReactNode;
  subtitle?: ReactNode;
  /** Optional control rendered next to the title (e.g. a segmented toggle). */
  titleExtra?: ReactNode;
  onBack: () => void;
  actions?: ReactNode;
  children: ReactNode;
  /** When set, wraps children in a centered max-width container with standard padding.
   *  Omit to let children manage their own layout. */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  actionsMaxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  noscroll?: boolean;
  /** Put actions on their own row on narrow screens when header controls need room. */
  stackHeaderOnMobile?: boolean;
  /**
   * Let the actions wrap onto a second row, at any width, rather than squeeze
   * the title — for pages with many header actions.
   */
  wrapActions?: boolean;
  /** Makes the title focusable from code, for moving focus to the page after a reload. */
  titleRef?: Ref<HTMLHeadingElement>;
}

const maxWidthClass: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
};

export function FormPageLayout({
  title,
  subtitle,
  titleExtra,
  onBack,
  actions,
  children,
  maxWidth,
  actionsMaxWidth: _actionsMaxWidth,
  noscroll,
  stackHeaderOnMobile = false,
  wrapActions = false,
  titleRef,
}: FormPageLayoutProps) {
  return (
    <div className="flex flex-col h-full">
      <div
        className={cn(
          'sticky top-0 z-10 flex shrink-0 items-center justify-between border-b bg-background px-4 py-3 sm:px-5',
          stackHeaderOnMobile && 'flex-wrap gap-y-2 sm:flex-nowrap',
          wrapActions && 'flex-wrap gap-x-4 gap-y-2',
        )}
      >
        <div
          className={cn(
            'flex min-w-0 items-center gap-2',
            stackHeaderOnMobile && 'w-full sm:w-auto',
            wrapActions && 'min-w-[12rem] flex-1',
          )}
        >
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onBack}>
            <ArrowLeft size={16} />
          </Button>
          <div className="min-w-0 flex-1">
            <h1
              ref={titleRef}
              tabIndex={titleRef ? -1 : undefined}
              className="min-w-0 truncate text-base font-semibold outline-none"
            >
              {title}
            </h1>
            {subtitle ? <p className="truncate text-sm text-muted-foreground">{subtitle}</p> : null}
          </div>
          {titleExtra ? <div className="ml-1 shrink-0">{titleExtra}</div> : null}
        </div>
        {actions && (
          <div
            className={cn(
              'flex items-center gap-2',
              stackHeaderOnMobile && 'ml-auto',
              wrapActions && 'ml-auto flex-wrap justify-end',
            )}
          >
            {actions}
          </div>
        )}
      </div>

      <div className={cn('flex-1', noscroll ? 'overflow-hidden' : 'overflow-y-auto')}>
        {maxWidth ? (
          <div className={`mx-auto ${maxWidthClass[maxWidth]} space-y-4 px-3 py-4 sm:px-4`}>
            {children}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
